import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format } from 'date-fns';

export const EscalationTimeline = ({ escalations }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return { icon: FiIcons.FiClock, color: 'amber' };
      case 'acknowledged':
        return { icon: FiIcons.FiCheckCircle, color: 'blue' };
      case 'resolved':
        return { icon: FiIcons.FiCheck, color: 'green' };
      case 'escalated':
        return { icon: FiIcons.FiAlertTriangle, color: 'red' };
      default:
        return { icon: FiIcons.FiCircle, color: 'slate' };
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'formal_notice':
        return FiIcons.FiFileText;
      case 'ministerial_alert':
        return FiIcons.FiAlertTriangle;
      case 'ppu_referral':
        return FiIcons.FiShield;
      case 'legal_action':
        return FiIcons.FiAlertOctagon;
      default:
        return FiIcons.FiFile;
    }
  };

  if (!escalations || escalations.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <SafeIcon icon={FiIcons.FiInbox} className="text-3xl mb-2" />
        <p className="text-sm">No escalations recorded</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline Line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"></div>

      {/* Timeline Items */}
      <div className="space-y-6">
        {escalations.map((escalation, idx) => {
          const statusInfo = getStatusIcon(escalation.status);
          const TypeIcon = getTypeIcon(escalation.type);

          return (
            <div key={idx} className="relative pl-14">
              {/* Status Indicator */}
              <div 
                className={clsx(
                  "absolute left-3.5 top-1 h-5 w-5 rounded-full border-2 border-white flex items-center justify-center",
                  `bg-${statusInfo.color}-500`
                )}
              >
                <SafeIcon 
                  icon={statusInfo.icon} 
                  className="text-white text-xs" 
                />
              </div>

              {/* Content Card */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={clsx(
                      "p-2 rounded-lg",
                      escalation.urgency === 'emergency' 
                        ? "bg-red-100 text-red-600"
                        : escalation.urgency === 'critical'
                        ? "bg-orange-100 text-orange-600"
                        : "bg-amber-100 text-amber-600"
                    )}>
                      <SafeIcon icon={TypeIcon} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-slate-800 text-sm">
                          {escalation.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h4>
                        <span className={clsx(
                          "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase",
                          escalation.urgency === 'emergency'
                            ? "bg-red-100 text-red-700"
                            : escalation.urgency === 'critical'
                            ? "bg-orange-100 text-orange-700"
                            : "bg-amber-100 text-amber-700"
                        )}>
                          {escalation.urgency}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {format(new Date(escalation.timestamp), 'dd MMM yyyy, HH:mm')}
                      </p>
                    </div>
                  </div>
                  <span className={clsx(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    escalation.status === 'resolved'
                      ? "bg-green-100 text-green-700"
                      : escalation.status === 'acknowledged'
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-100 text-slate-700"
                  )}>
                    {escalation.status}
                  </span>
                </div>

                {/* Recipient Info */}
                <div className="mb-3 p-3 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-slate-500 font-medium">To:</span>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {escalation.recipient.officer}
                      </p>
                      <p className="text-slate-600">{escalation.recipient.ministry}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 font-medium">From:</span>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {escalation.initiatedBy}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {escalation.notes && (
                  <div className="mb-3">
                    <p className="text-xs text-slate-600 italic">
                      "{escalation.notes}"
                    </p>
                  </div>
                )}

                {/* CC Authorities */}
                {escalation.ccAuthorities && escalation.ccAuthorities.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      CC:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {escalation.ccAuthorities.map((auth, i) => (
                        <span 
                          key={i}
                          className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded"
                        >
                          {auth.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Flags */}
                <div className="flex gap-2 flex-wrap">
                  {escalation.legalAction && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold rounded">
                      <SafeIcon icon={FiIcons.FiAlertOctagon} />
                      Legal Action Recommended
                    </span>
                  )}
                  {escalation.attachEvidence && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-[10px] font-medium rounded">
                      <SafeIcon icon={FiIcons.FiPaperclip} />
                      Evidence Attached
                    </span>
                  )}
                  {escalation.requestMeeting && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 text-[10px] font-medium rounded">
                      <SafeIcon icon={FiIcons.FiCalendar} />
                      Meeting Requested
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                  <button className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-1">
                    <SafeIcon icon={FiIcons.FiEye} />
                    View Full Record
                  </button>
                  {escalation.status === 'pending' && (
                    <button className="flex-1 px-3 py-1.5 bg-grn-navy text-white rounded text-xs font-medium hover:bg-blue-900 transition-colors flex items-center justify-center gap-1">
                      <SafeIcon icon={FiIcons.FiCheck} />
                      Mark Acknowledged
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};